export const MONTH_NAMES = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember'
]

export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

export const DAY_NAMES = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']

export const DAY_SHORT = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab']

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_PATTERN = /^(\d{4})-(\d{2})$/
const DAY_MS = 86400000

const pad = (value: number): string => String(value).padStart(2, '0')

export const toDateString = (date: Date): string => {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

export const parseDate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day))
}

export const isValidDateString = (value: unknown): value is string => {
    if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false

    const year = Number(value.slice(0, 4))
    if (year < 1970 || year > 2100) return false

    return toDateString(parseDate(value)) === value
}

export const isValidMonthString = (value: unknown): value is string => {
    if (typeof value !== 'string' || !MONTH_PATTERN.test(value)) return false

    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(5, 7))

    return year >= 1970 && year <= 2100 && month >= 1 && month <= 12
}

export const addDays = (value: string, days: number): string => {
    const date = parseDate(value)
    date.setUTCDate(date.getUTCDate() + days)
    return toDateString(date)
}

export const diffInDays = (from: string, to: string): number => {
    return Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / DAY_MS)
}

export const monthOf = (value: string): string => value.slice(0, 7)

export const yearOf = (value: string): number => Number(value.slice(0, 4))

export const daysInMonth = (month: string): number => {
    const [year, monthIndex] = month.split('-').map(Number)
    return new Date(Date.UTC(year, monthIndex, 0)).getUTCDate()
}

export const startOfMonth = (month: string): string => `${month}-01`

export const endOfMonth = (month: string): string => `${month}-${pad(daysInMonth(month))}`

export const addMonths = (month: string, count: number): string => {
    const [year, monthIndex] = month.split('-').map(Number)
    const date = new Date(Date.UTC(year, monthIndex - 1 + count, 1))
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`
}

export const startOfWeek = (value: string): string => {
    const offset = (parseDate(value).getUTCDay() + 6) % 7
    return addDays(value, -offset)
}

export const minDate = (a: string, b: string): string => (a < b ? a : b)

export const maxDate = (a: string, b: string): string => (a > b ? a : b)

/**
 * Today's calendar date in a given time zone, as YYYY-MM-DD.
 * Used on the server so every page renders the same "today" on both sides.
 */
export const todayIn = (timeZone = 'Asia/Jakarta'): string => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date())

    const get = (type: string): string => parts.find(part => part.type === type)?.value ?? ''

    return `${get('year')}-${get('month')}-${get('day')}`
}

/**
 * The visitor's local calendar date. Browser only: call it inside effects or event handlers.
 */
export const localToday = (): string => {
    const now = new Date()
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
